import {Injectable, OnDestroy} from "@angular/core";
import {Subscription} from "rxjs";
import {EntityData, FedeltaImageData} from "@fedelta-pos/data-client";
import {SessionModel} from "../orchestration/session.model";
import {TranslateService} from "ng2-translate";
import {IdentityModel} from "../identity/identity.model";
import {OrderingContextModel} from "../ordering-context/ordering-context.model";
import {OrchestrationEvents} from "../orchestration/orchestration-events";
import {PromoEvents} from "./promo-events";
import {MenuEvents} from "../menu/menu-events";
import {IdentityEvents} from "../identity/identity-events";
import {
    AppPromoAction,
    AppPromoData,
    AppPromoPromotedSellableData,
    AppPromoSellableData
} from "../identity/member-summary-entity.model";
import {OrderItem} from "../order/order";
import {OrderEvents} from "../order/order-events";
import {MenuModel} from "../menu/menu.model";
import {OrderModel} from "../order/order.model";
import {MenuCategoryData, MenuItemData} from "../menu/menu-entity.model";
import {OrderingContextEvents} from "../ordering-context/ordering-context-events";

export class PromotedSellable {
    constructor(public sellableId: number,
                public featurePromotedMenuItems: boolean,
                public promotionalImage: FedeltaImageData,
                public useImageInPromoOnly: boolean,
                public menuItemFeatureTag: string) {
    }
}

@Injectable()
export class PromoModel implements OnDestroy {
    public activatedPromo: AppPromoData;

    private promotedSellables: PromotedSellable[] = [];

    // Prevent re-prompting after a "checkout" action is deferred.
    // This flag is cleared when a non-deferred checkout action is started.
    // Note: this does NOT stop multiple checkout promos - that is done by the limit on the individual promo.
    private promoPromptedForCurrentCheckout: boolean = false;

    private timesPromosPrompted: number = 0;
    private promptedPromos: { [promoId: string]: { timesPrompted: number } } = {};
    private promptedSellables: { [sellableId: number]: { timesPrompted: number } } = {};

    // Avoid injection dependencies on OrderModel and MenuModel
    private _orderModel: OrderModel;
    private _menuModel: MenuModel;

    private subscriptions: Subscription = new Subscription();

    constructor(private sessionModel: SessionModel,
                private orderingContextModel: OrderingContextModel,
                private identityModel: IdentityModel,
                private translate: TranslateService) {
        console.log("--- PromoModel");

        this.subscriptions.add(OrchestrationEvents.sessionReset$.subscribe(() => {
            this.promoPromptedForCurrentCheckout = false;
            this.timesPromosPrompted = 0;
            this.promptedPromos = {};
            this.promptedSellables = {};
        }));
        this.subscriptions.add(IdentityEvents.currentMember$.subscribe(() => this.findPromotedSellables()));
        this.subscriptions.add(OrderEvents.orderChanged$.subscribe(() => this.findPromotedSellables()));
        this.subscriptions.add(OrderingContextEvents.orderType$.subscribe(() => this.findPromotedSellables()));
        this.subscriptions.add(OrderingContextEvents.storeDetailsChanged$.subscribe((storeChanged: boolean) => storeChanged && this.findPromotedSellables()));
    }

    set orderModel(orderModel: OrderModel) {
        this._orderModel = orderModel;
    }

    set menuModel(menuModel: MenuModel) {
        this._menuModel = menuModel;
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    public setActivatedPromo(promo: AppPromoData): void {
        this.activatedPromo = promo;
        PromoEvents.activatedPromo$.next(promo);
    }

    public hasOrderedTriggerSellable(promo: AppPromoData, justOrdered: boolean = false): boolean {
        return this._orderModel.hasOrderedItemPersonally((item: OrderItem) => (!justOrdered || item.justOrdered) && promo.isTriggeredBySellable(item.sellable));
    }

    /**
     * Gets the list of currently promoted sellables and their corresponding promotional image and/or feature tag.
     * "Currently promoted" sellables are those promoted by an untriggered promo, or by a promo triggered by a product that has been ordered.
     */
    public findPromotedSellables(): void {
        this.promotedSellables = [];
        if (this.identityModel.currentMember) {
            this.identityModel.currentMember.promos
                .filter((promo: AppPromoData) => promo.isAvailableForOrderType(this.orderingContextModel.orderType) &&
                    promo.promotedSellables.length &&
                    (!promo.triggered || this.hasOrderedTriggerSellable(promo)))
                .forEach((promo: AppPromoData) => this.promotedSellables = this.promotedSellables.concat(
                    promo.promotedSellables.map((promotedSellable: AppPromoPromotedSellableData) => new PromotedSellable(promotedSellable.sellable.sellableId, promo.featurePromotedMenuItems, promotedSellable.productImage, promo.itemImagesInPromoOnly, promo.menuItemFeatureTag))));
        }
        PromoEvents.promotedSellablesRefreshed$.next();
    }

    public isFeaturedSellable(sellableId: number): boolean {
        return this.promotedSellables.some((promotedSellable: PromotedSellable) => promotedSellable.sellableId === sellableId && promotedSellable.featurePromotedMenuItems);
    }

    /**
     * Selects a popup promo and menu item from the supplied list of promos based on the most recently ordered items from the current order.
     *
     * Rules for picking available popup promos:
     * 1. The promo must have been prompted fewer times than its popup limit (if set);
     * 2. The promo must be triggered and configured to "popupWhenOrdered";
     * 3. The promo must be triggered by a product which has just been ordered;
     * 4. If the promo has promoted sellables:
     *      a) None of the promoted sellables may not be already on the order (if "suppressPopupIfOrdered" is set);
     *      b) One or more promoted sellables must be:
     *          i)   not already prompted; and
     *          ii)  not on the order; and
     *          iii) on the menu;
     * 5. The promo must have already been prompted a number of times equal to the minimum for all promos meeting criteria 1 through 4.
     *
     * Additional filtering is then done to filter out promoted sellables which have already been prompted, or are not on the menu.
     */
    public selectTriggeredPopupWhenOrderedPromo(promos: AppPromoData[]): {
        promo: AppPromoData,
        menuItem?: MenuItemData
    } {
        if (this.sessionModel.currentApp.promoPopupLimit && this.timesPromosPrompted >= this.sessionModel.currentApp.promoPopupLimit) {
            return undefined;
        }

        promos = [...promos];

        // Add individual promos specified directly on the recently ordered items
        this._orderModel.getRecentlyOrderedItems().forEach((item: OrderItem) => {
            if (item.sellable.promotedSellableId && this.getTimesSellablePrompted(item.sellable.promotedSellableId) === 0) {
                const promo: AppPromoData = EntityData.create(AppPromoData);
                promo.id = item.sellable.sellableId.toString();
                promo.name = item.menuItem.name;
                promo.triggered = true;
                const triggerSellable: AppPromoSellableData = EntityData.create(AppPromoSellableData);
                triggerSellable.sellableId = item.sellable.sellableId;
                promo.triggerSellables = [triggerSellable];
                const promotedSellable: AppPromoPromotedSellableData = EntityData.create(AppPromoPromotedSellableData);
                promotedSellable.sellable.sellableId = item.sellable.promotedSellableId;
                promotedSellable.promoteIfOrdered = false;
                promo.promotedSellables = [promotedSellable];
                promo.showPopupWhenOrdered = true;
                promo.popupAction = AppPromoAction.ORDER_PRODUCT;
                promo.suppressPopupIfOrdered = true;
                promo.suppressPopupProducts = true;
                promo.popupLimit = 1;
                promo.showDescriptionOnPopup = true;
                promos.push(promo);

                // Prioritise individual promos
                this.getTimesPromoPrompted(promo.id, -1);
            }
        });

        return !this._orderModel.hasOrderedItems ? undefined : this.selectPopupPromo(
            promos.filter((promo: AppPromoData) =>
                promo.isAvailableForOrderType(this.orderingContextModel.orderType) &&
                promo.triggered && promo.showPopupWhenOrdered &&
                (!promo.popupLimit || this.getTimesPromoPrompted(promo.id) < promo.popupLimit) &&
                this.hasOrderedTriggerSellable(promo, true) &&
                (!promo.promotedSellables.length || this.canPromoteSellablesAsPopup(promo))));
    }

    /**
     * Selects a popup promo and menu item based on the most recently ordered items in the current order.
     *
     * Rules for picking available popup promos:
     * 1. The promo must have been prompted fewer times than its popup limit (if set);
     * 2. The promo must be triggered and configured to "popupOnCheckout";
     * 3. The promo must either be untriggered or be triggered by a product in the order;
     * 4. If the promo has promoted sellables:
     *      a) None of the promoted sellables may be already on the order (if "suppressPopupIfOrdered" is set);
     *      b) One or more promoted sellables must be:
     *          i)   not already prompted; and
     *          ii)  not on the order; and
     *          iii) currently on the menu (respecting schedules);
     * 5. The promo must have already been prompted a number of times equal to the minimum for all promos meeting criteria 1 through 4.
     *
     * Additional filtering is then done to filter out promoted sellables which have already been prompted, or are not on the menu.
     */
    public selectCheckoutPopupPromo(): { promo: AppPromoData, menuItem?: MenuItemData } {
        if (this.promoPromptedForCurrentCheckout) {
            return undefined;
        }
        const selection: {
            promo: AppPromoData,
            menuItem?: MenuItemData
        } = this.selectPopupPromo(this.availableCheckoutPopupPromos);
        if (selection) {
            this.promoPromptedForCurrentCheckout = true;
        }
        return selection;
    }

    public get availableCheckoutPopupPromos(): AppPromoData[] {
        if (this.sessionModel.currentApp.promoPopupLimit && this.timesPromosPrompted >= this.sessionModel.currentApp.promoPopupLimit) {
            return [];
        }
        return this.identityModel.currentMember && this.identityModel.currentMember.promos.filter((promo: AppPromoData) =>
            promo.isAvailableForOrderType(this.orderingContextModel.orderType) &&
            promo.showPopupOnCheckout &&
            (!promo.popupLimit || this.getTimesPromoPrompted(promo.id) < promo.popupLimit) &&
            (!promo.triggered || this.hasOrderedTriggerSellable(promo)) &&
            (!promo.promotedSellables.length || this.canPromoteSellablesAsPopup(promo)));
    }

    public notifyCheckoutStarted(deferred: boolean): void {
        if (!deferred) {
            this.promoPromptedForCurrentCheckout = false;
        }
    }

    public cardPromoIsTriggered(promo: AppPromoData): boolean {
        return promo.triggered && promo.showAppPromoCard &&
            this.hasOrderedTriggerSellable(promo) &&
            (!promo.promotedSellables.length || this.canPromoteSellablesAsCard(promo, true));
    }

    public cardPromoIsSuppressed(promo: AppPromoData): boolean {
        return promo.suppressPromoCardIfAnyOrdered ? this._orderModel.personalOrderStarted
            : promo.suppressPromoCardIfOrdered ? this.hasOrderedPromotedSellables(promo)
                : false;
    }

    public hasOrderedPromotedSellables(promo: AppPromoData): boolean {
        return promo.promotedSellables.some((promoted: AppPromoPromotedSellableData) => this._orderModel.hasOrderedSellablePersonally(promoted.sellable.sellableId));
    }

    public canPromoteSellablesAsCard(promo: AppPromoData, suppressProducts: boolean): boolean {
        return promo.promotedSellables.some((promoted: AppPromoPromotedSellableData) => !!this.getPromotableMenuItem(promoted, suppressProducts && promo.suppressPromoCardProducts));
    }

    public canPromoteSellablesAsPopup(promo: AppPromoData): boolean {
        return !(promo.suppressPopupIfOrdered && this.hasOrderedPromotedSellables(promo)) &&
            promo.promotedSellables.some((promoted: AppPromoPromotedSellableData) => this.getTimesSellablePrompted(promoted.sellable.sellableId) === 0 && !!this.getPromotableMenuItem(promoted, promo.suppressPopupProducts));
    }

    public canPromoteSellablesAsCategory(promo: AppPromoData): boolean {
        return !(promo.suppressCategoryIfOrdered && this.hasOrderedPromotedSellables(promo)) &&
            promo.promotedSellables.some((promoted: AppPromoPromotedSellableData) => !!this.getPromotableMenuItem(promoted, promo.suppressCategoryProducts));
    }

    public getPromotableMenuItem(promoted: AppPromoPromotedSellableData, suppressProducts: boolean): MenuItemData {
        if (!MenuEvents.currentMenu.value || (suppressProducts && !promoted.promoteIfOrdered && this._orderModel.hasOrderedSellablePersonally(promoted.sellable.sellableId))) {
            return undefined;
        }
        const menuItem: MenuItemData = this._menuModel.findMergedItemForSellable(promoted.sellable.sellableId, this.orderingContextModel.orderType);
        return menuItem && this._menuModel.itemIsAvailable(menuItem) ? menuItem : undefined;
    }

    public getPromotedFeatureTag(category: MenuCategoryData, menuItem: MenuItemData): string {

        // Promoted category - use its feature tag if specified
        if (category && category.itemFeatureTag) {
            menuItem.promotedFeatureTag = category.itemFeatureTag;
            return menuItem.promotedFeatureTag;
        }

        if (!this.promotedSellables) {
            return undefined;
        }

        let isPromoted: boolean = false;
        for (const promotedSellable of this.promotedSellables) {
            if (promotedSellable.featurePromotedMenuItems && promotedSellable.sellableId === menuItem.sellable.sellableId) {
                isPromoted = true;
                if (promotedSellable.menuItemFeatureTag) {
                    menuItem.promotedFeatureTag = promotedSellable.menuItemFeatureTag;
                    return menuItem.promotedFeatureTag;
                }
            }
        }
        menuItem.promotedFeatureTag = isPromoted ? this.translate.instant("woa.label.order.featured") : undefined;
        return menuItem.promotedFeatureTag;
    }

    public getPromotionalImage(category: MenuCategoryData, sellableId: number, forPromo?: boolean): FedeltaImageData {
        if (forPromo === undefined) {
            forPromo = category && category.id == -1;
        }
        const promotedSellable: PromotedSellable = this.promotedSellables.find((promotedSellable: PromotedSellable) => promotedSellable.sellableId === sellableId && (forPromo || !promotedSellable.useImageInPromoOnly));
        return promotedSellable && promotedSellable.promotionalImage && promotedSellable.promotionalImage.id ? promotedSellable.promotionalImage : undefined;
    }

    private selectPopupPromo(promos: AppPromoData[]): { promo: AppPromoData, menuItem?: MenuItemData } {
        if (!promos.length) {
            return undefined;
        }

        const minTimesPrompted: number = Math.min(...promos.map((promo: AppPromoData) => this.getTimesPromoPrompted(promo.id)));
        const lfuPromos: AppPromoData[] = promos.filter((promo: AppPromoData) => this.getTimesPromoPrompted(promo.id) === minTimesPrompted);
        const promo: AppPromoData = lfuPromos[Math.floor(Math.random() * lfuPromos.length)];
        if (!promo.promotedSellables.length) {
            this.timesPromosPrompted++;
            this.promptedPromos[promo.id].timesPrompted = Math.max(1, this.promptedPromos[promo.id].timesPrompted + 1);
            return {promo: promo};
        }
        for (const promoted of promo.promotedSellables) {
            if (this.getTimesSellablePrompted(promoted.sellable.sellableId) == 0) {
                const menuItem: MenuItemData = this.getPromotableMenuItem(promoted, promo.suppressPopupProducts);
                if (menuItem) {
                    this.timesPromosPrompted++;
                    this.promptedPromos[promo.id].timesPrompted = Math.max(1, this.promptedPromos[promo.id].timesPrompted + 1);
                    this.promptedSellables[promoted.sellable.sellableId].timesPrompted++;
                    return {promo: promo, menuItem: menuItem};
                }
            }
        }

        return undefined;
    }

    private getTimesPromoPrompted(promoId: string, start: number = 0): number {
        if (!(promoId in this.promptedPromos)) {
            this.promptedPromos[promoId] = {timesPrompted: start};
        }
        return this.promptedPromos[promoId].timesPrompted;
    }

    private getTimesSellablePrompted(sellableId: number, start: number = 0): number {
        if (!(sellableId in this.promptedSellables)) {
            this.promptedSellables[sellableId] = {timesPrompted: start};
        }
        return this.promptedSellables[sellableId].timesPrompted;
    }

}



// WEBPACK FOOTER //
// ./src/app/promo/promo.model.ts